import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ImageSlider from "./ImageSlider"; 
import "../assets/css/OrderNow.css"; 
import bg1 from "../assets/images/frame1.svg";
import bg2 from "../assets/images/frame2.svg";
import bg3 from "../assets/images/frame3.svg";
import bg4 from "../assets/images/frame4.svg";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Helmet } from "react-helmet-async";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Module-level so the campaign effect below has a stable dependency.
const BACKGROUND_IMAGES = [bg1, bg2, bg3, bg4];

const OrderNow = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [countdowns, setCountdowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backgroundMap, setBackgroundMap] = useState({});
  const navigate = useNavigate();


  useEffect(() => {
    // Fetch campaigns from the API
    const fetchCampaigns = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/campaign/get-campaigns`); 
        setCampaigns(response.data.campaigns || []);
        setLoading(false);
      } catch (err) {
        // Nothing to show either way, so fall through to the "no campaigns
        // running" state rather than surfacing a technical error.
        console.error("Failed to fetch campaigns:", err);
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  useEffect(() => {
    const calculateCountdown = (endDate) => {
      const targetDate = new Date(endDate);
      const currentDate = new Date();
      const timeDiff = targetDate - currentDate;

      if (timeDiff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };
    if (campaigns.length > 0) {
      const updateCountdowns = () => {
        setCountdowns(
          campaigns.map((campaign) => ({
            title: campaign.name,
            countdown: calculateCountdown(campaign.end_date),
            endDate: campaign.end_date,
          }))
        );
      };

      const map = {};
      campaigns.forEach((campaign) => {
        const randomIndex = Math.floor(Math.random() * BACKGROUND_IMAGES.length);
        map[campaign._id] = BACKGROUND_IMAGES[randomIndex];
      });
      setBackgroundMap(map);

      updateCountdowns();
      const intervalId = setInterval(updateCountdowns, 1000);

      return () => clearInterval(intervalId);
    }
  }, [campaigns]);

  const handleQuickOrder = (campaignId) => {
    navigate(`/quick-order/${campaignId}`); // Navigate with campaign ID
  };


  const showDeliveryInfo = (campaign) => {
    Swal.fire({
      title: "Delivery & Refund Policy",
      html: `
        <div style="text-align:left; font-size:1.08rem; color:#1C4745;">
          <b>When will my order be delivered?</b><br/>
          • The campaign closes on <b>${new Date(campaign.end_date).toDateString()}</b>.<br/>
          • All deliveries are made within <b>7 business days</b> after the campaign ends.<br/><br/>
          <b>Order Confirmation:</b><br/>
          • Your order is confirmed immediately and automatically after checkout.<br/><br/>
          <b>Refund Policy:</b><br/>
          • <b>All sales are final.</b>If your order cannot be fulfilled, you’ll receive a full refund within 48 hours after the campaign ends.<br/>
          • For any issues, contact us at <a href="mailto:info@farmerboaz.com">info@farmerboaz.com</a>.<br/><br/>
           <b>Stay Connected:</b><br/>
        • <a href="https://discord.gg/X47Vt45XWh" target="_blank" rel="noopener noreferrer" style="color: #5865F2; font-weight: 600;">Join our Discord community</a> for more updates, communication, and transparency!</b><br/></b><br/>
           <b>Important Notice:</b><br/>
          • This meat is provided as part of a community share and has not been inspected by provincial or federal food safety authorities.<br/>
          • It is intended only for private, non-commercial use among community members.
          • Resale or serving to the general public is not permitted.
        </div>
      `,
      confirmButtonText: "Got it!",
      width: 480,
      confirmButtonColor: "#1C4745",
      background: "#fff",
      customClass: {
        popup: 'swal2-rounded'
      }
    });
  };

  return (
          <>
          <Helmet>
            <title>Order Now – Farmer Boaz</title>
            <meta name="description" content="Browse open Farmer Boaz community share campaigns and order fresh, local meat. Simple, safe, and sustainable." />
          </Helmet>
    <div className="order-now-container">
      <div className="image-slider-container">
        <ImageSlider />
      </div>

      <header className="order-page-head">
        <span className="eyebrow">Open campaigns</span>
        <h1>Order farm-fresh, together</h1>
        <p>
          Each campaign pools orders from the community so a whole animal or harvest can be shared
          fairly. Pick one below and reserve your portion before the timer runs out.
        </p>
      </header>

      <div className="campaigns-list" id="order-now">
        {loading &&
          [0, 1, 2].map((key) => <div className="campaign-skeleton" key={key} aria-hidden="true" />)}

        {!loading && campaigns.length === 0 && (
          <div className="campaigns-empty">
            <h3>No campaigns running at the moment</h3>
            <p>
              There is no community share open right now. We will reach out as soon as the next
              one opens — follow us on{" "}
              <a
                href="https://www.instagram.com/farmerboaz?igsh=MXNrdGMwbjdqcW5lNQ=="
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>{" "}
              or join our{" "}
              <a href="https://discord.gg/X47Vt45XWh" target="_blank" rel="noopener noreferrer">
                Discord community
              </a>{" "}
              to hear first.
            </p>
          </div>
        )}

        {!loading &&
          campaigns.map((campaign, index) => (
            <article
              key={campaign._id}
              className="campaign-box"
              style={{ backgroundImage: `url(${backgroundMap[campaign._id]})` }}
            >
              <div className="campaign-overlay">
                <h3>{campaign.name}</h3>
                <p>
                  <span className={`status ${campaign.status.toLowerCase()}`}>
                    {campaign.status}
                  </span>
                </p>
                <div className="campaign-details">
                  <p>
                    <strong>Opens:</strong> {new Date(campaign.start_date).toDateString()}
                  </p>
                  <p>
                    <strong>Closes:</strong> {new Date(campaign.end_date).toDateString()}
                  </p>
                  <p>{campaign.description}</p>
                </div>

                {campaign.status.toLowerCase() === "active" && (
                  <>
                    <div className="countdown-timer">
                      {countdowns[index] && (
                        <p>
                          <strong>Time remaining:</strong> {countdowns[index].countdown.days}d{" "}
                          {countdowns[index].countdown.hours}h {countdowns[index].countdown.minutes}m{" "}
                          {countdowns[index].countdown.seconds}s
                        </p>
                      )}
                    </div>

                    <div className="campaign-action-row">
                      <button
                        className="quick-order-button"
                        onClick={() => handleQuickOrder(campaign._id)}
                      >
                        Quick Order
                      </button>
                      <button
                        className="campaign-info-btn"
                        onClick={() => showDeliveryInfo(campaign)}
                        aria-label={`Delivery and refund info for ${campaign.name}`}
                        title="See delivery & refund info"
                        type="button"
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </article>
          ))}
      </div>
    </div>
    </>
  );
};

export default OrderNow;
